"use client";

import { useEffect, useRef, useState } from "react";
import { LazyInstrumentationDebugPanel } from "@/components/instrumentation/InstrumentationDebugPanel/LazyInstrumentationDebugPanel";
import type { AnalyticsInstrumentationEvents } from "@/core/analytics/analytics.events";
import { createGtmSink } from "@/core/analytics/gtm/gtm.sink";
import { PageViewReporter } from "@/core/analytics/PageViewReporter/PageViewReporter";
import { createSegmentSink } from "@/core/analytics/segment/segment.sink";
import { ConsentBanner, createConsentStack } from "@/core/consent";
import { createGrowthBookSink } from "@/core/featureFlags/growthbook.sink";
import { isDebugAssessmentPanelDomainAllowed } from "@/core/host";
import { useI18n } from "@/core/i18n";
import { createInstrumentationBus } from "@/core/instrumentation/bus/bus";
import { setActiveBus } from "@/core/instrumentation/instrumentation.runtime";
import { createDatadogSink } from "@/core/monitoring/datadog.sink";

/**
 * Tracked events and page views seen before consent is settled are held and sent if a grant
 * arrives, so an experiment's exposure and the conversions after it are counted together or not
 * at all, and the page a visitor landed on is counted once an opt-out default applies. Identify
 * is not held: it carries contact details.
 */
const HELD_UNTIL_ANSWERED = ["analytics.track", "analytics.page"] as const;

/**
 * The COMPOSITION ROOT: consent on one side, vendors on the other, one wire
 * between them. Consent assembles itself (`createConsentStack`: engine,
 * sources, recorder, reopen, live store) and renders its own banner; this
 * file never learns which CMP that is. What it does name is every vendor —
 * the sinks on the bus are instrumentation's concern, and this is the one
 * place they are listed.
 *
 * Rendered from the root layout beside the app tree, not around it (see
 * `consent.runtime` for why).
 *
 * Exactly one instance per page. React StrictMode runs the effect twice in
 * development — mount, cleanup, mount — and the cleanup stops the first
 * instance before the second is created, so a recreate is fine and two live
 * instances are impossible. The composition effect deliberately has no
 * dependencies: nothing about the route may restart the engine (Datadog would
 * drop and resume collection, and every vendor would re-run its lifecycle).
 * The one route fact the recorder reports — the language the notice is
 * presented in — reaches it through a ref read at record time, never as an
 * effect input. `PageViewReporter`, which is route-driven by nature, is a
 * child for the same reason: the route it reads re-renders it alone, never
 * this component.
 */
export function InstrumentationProvider() {
  // Recorded as the language the notice was presented in: the route locale.
  // Exact for a banner that follows the route; for one that shows a single
  // wording it names the language of the page around the banner (see the
  // mounted banner's notice snapshot). Held in a ref so a language change
  // updates what the next decision is recorded under without touching the
  // composition effect.
  const {
    currentLangCode
  } = useI18n();
  const routeLanguageCodeRef = useRef(currentLangCode);
  routeLanguageCodeRef.current = currentLangCode;

  // Resolved in an effect rather than during render: the hostname is a
  // client-only fact, so reading it while rendering would make the server and
  // the first client render disagree. Off the allowlist the debug panel's
  // dynamic import never runs at all, so its chunk is never downloaded on a
  // host that would only render null.
  const [isDebugPanelAllowed, setIsDebugPanelAllowed] = useState(false);

  // The engine's liveness, mirrored into render so the page-view reporter can
  // be withheld until there is something to publish to. This component creates
  // the engine, so it is the one place that knows; making the reporter probe
  // for it instead would have it subscribe to consent just to learn a fact
  // that has nothing to do with consent.
  const [isEngineMounted, setIsEngineMounted] = useState(false);
  useEffect(() => {
    setIsDebugPanelAllowed(isDebugAssessmentPanelDomainAllowed());
  }, []);
  useEffect(() => {
    const consent = createConsentStack({
      // Read at record time: a decision made after a language change is
      // recorded under the language the notice was showing at that moment.
      routeLanguageCode: () => routeLanguageCodeRef.current
    });
    const bus = createInstrumentationBus<AnalyticsInstrumentationEvents>({
      subscribers: [createDatadogSink(), createSegmentSink(), createGtmSink(), createGrowthBookSink()],
      holdUntilAnswered: HELD_UNTIL_ANSWERED
    });

    // The one wire: the bus observes the engine, subscribed BEFORE the stack
    // starts so the very first resolution (a stored decision replays
    // synchronously on start) reaches the vendors — after React and the
    // recorder, which the stack wired at construction.
    bus.start(consent.engine.getStatus());
    const unsubscribeBus = consent.engine.subscribe(bus.setConsentStatus);
    setActiveBus(bus);
    consent.start();
    setIsEngineMounted(true);
    return () => {
      setIsEngineMounted(false);
      setActiveBus(null);
      unsubscribeBus();
      bus.stop();
      consent.stop();
    };
  }, []);
  return <>
      {/* Beside the app tree, so every route gets a page view — including the
          route groups that mount no provider tree. Mounting it IS the first
          route's trigger, which is why it waits for the engine. */}
      {isEngineMounted && <PageViewReporter />}
      <ConsentBanner />
      {/* The panel repeats this hostname check itself, as defense in depth. */}
      {isDebugPanelAllowed && <LazyInstrumentationDebugPanel />}
    </>;
}